import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService, chatService } from '../api';
import AuthModal from '../components/auth/AuthModal';
import AdCard from '../components/AdCard';
import Seo from '../components/Seo';
import './SellerProfilePage.css';

const ACCOUNT_TYPE_LABELS = { individual: 'Частное лицо', realtor: 'Риелтор' };

const Star = ({ filled, size = 20, onClick, onMouseEnter }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill={filled ? '#FFB300' : 'none'} stroke="#FFB300" strokeWidth="1.5"
        onClick={onClick} onMouseEnter={onMouseEnter}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const StarRating = ({ value, size }) => (
    <div className="seller-profile__stars">
        {[1, 2, 3, 4, 5].map(i => <Star key={i} filled={i <= Math.round(value)} size={size} />)}
    </div>
);

const StarInput = ({ value, onChange }) => {
    const [hover, setHover] = useState(0);
    return (
        <div className="seller-profile__stars" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    filled={i <= (hover || value)}
                    size={26}
                    onClick={() => onChange(i)}
                    onMouseEnter={() => setHover(i)}
                />
            ))}
        </div>
    );
};

const reviewWord = (n) => {
    const mod10 = n % 10, mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'отзыв';
    if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'отзыва';
    return 'отзывов';
};

const formatRegisteredAt = (dateStr) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
};

const SellerProfilePage = () => {
    const { userId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [phoneRevealed, setPhoneRevealed] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [chatBusy, setChatBusy] = useState(false);

    const [activeTab, setActiveTab] = useState('reviews');

    const [reviews, setReviews] = useState([]);
    const [reviewsPage, setReviewsPage] = useState(1);
    const [reviewsHasMore, setReviewsHasMore] = useState(false);
    const [reviewsLoading, setReviewsLoading] = useState(true);

    const [ads, setAds] = useState([]);
    const [adsLoading, setAdsLoading] = useState(true);

    const [formRating, setFormRating] = useState(0);
    const [formContent, setFormContent] = useState('');
    const [formBusy, setFormBusy] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const isOwnProfile = user?.id === userId;

    const loadProfile = useCallback(async () => {
        try {
            const { data } = await userService.getProfile(userId);
            setProfile(data);
            if (data.myRating) {
                setFormRating(data.myRating);
                setFormContent(data.myReviewContent || '');
            }
        } catch {
            setProfile(null);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const loadReviews = useCallback(async (page) => {
        setReviewsLoading(true);
        try {
            const { data } = await userService.getReviews(userId, page);
            setReviews(prev => page === 1 ? data.reviews : [...prev, ...data.reviews]);
            setReviewsHasMore(data.hasMore);
        } catch {
            if (page === 1) setReviews([]);
        } finally {
            setReviewsLoading(false);
        }
    }, [userId]);

    useEffect(() => { setLoading(true); loadProfile(); }, [loadProfile]);
    useEffect(() => { setReviewsPage(1); loadReviews(1); }, [loadReviews]);

    useEffect(() => {
        setAdsLoading(true);
        userService.getUserAds(userId)
            .then(({ data }) => setAds(data.items ?? []))
            .catch(() => setAds([]))
            .finally(() => setAdsLoading(false));
    }, [userId]);

    const handleShowMoreReviews = () => {
        const next = reviewsPage + 1;
        setReviewsPage(next);
        loadReviews(next);
    };

    const handleShowPhone = () => {
        if (!user) { setAuthOpen(true); return; }
        setPhoneRevealed(true);
    };

    const handleStartChat = async () => {
        if (!user) { setAuthOpen(true); return; }
        if (chatBusy) return;
        setChatBusy(true);
        try {
            const { data: chat } = await chatService.createOrGetChat(userId);
            navigate('/profile', { state: { tab: 'messages', chatId: chat.id } });
        } catch {
            /* ignore */
        } finally {
            setChatBusy(false);
        }
    };

    const submitReview = async () => {
        if (!user) { setAuthOpen(true); return; }
        if (formRating < 1 || formBusy) return;
        setFormBusy(true);
        try {
            await userService.submitReview(userId, formRating, formContent || null);
            await loadProfile();
            setReviewsPage(1);
            await loadReviews(1);
        } catch {
            alert('Не удалось отправить отзыв. Попробуйте позже.');
        } finally {
            setFormBusy(false);
        }
    };

    const handleDeleteReview = async () => {
        if (deleteBusy) return;
        if (!window.confirm('Удалить ваш отзыв?')) return;
        setDeleteBusy(true);
        try {
            await userService.deleteMyReview(userId);
            setFormRating(0);
            setFormContent('');
            await loadProfile();
            setReviewsPage(1);
            await loadReviews(1);
        } catch {
            alert('Не удалось удалить отзыв. Попробуйте позже.');
        } finally {
            setDeleteBusy(false);
        }
    };

    if (loading) return <div className="seller-profile"><div className="seller-profile__loading">Загрузка...</div></div>;
    if (!profile) return <div className="seller-profile"><div className="seller-profile__loading">Пользователь не найден</div></div>;

    return (
        <div className="seller-profile">
            <Seo
                title={`${profile.name} — объявления и отзывы`}
                description={`${ACCOUNT_TYPE_LABELS[profile.accountType] || 'Частное лицо'} ${profile.name} на Dealzy${profile.reviewsCount ? ` — ${profile.reviewsCount} ${reviewWord(profile.reviewsCount)}` : ''}.`}
                image={profile.avatarUrl}
            />
            <div className="seller-profile__container">

                <div className="seller-profile__header">
                    <div className="seller-profile__avatar">
                        {profile.avatarUrl
                            ? <img src={profile.avatarUrl} alt={profile.name} />
                            : (profile.name?.charAt(0).toUpperCase() || '?')
                        }
                    </div>
                    <div className="seller-profile__header-info">
                        <p className="seller-profile__role">{ACCOUNT_TYPE_LABELS[profile.accountType] || 'Частное лицо'}</p>
                        <h1 className="seller-profile__name">{profile.name}</h1>
                        <div className="seller-profile__rating-row">
                            <StarRating value={profile.averageRating || 0} size={18} />
                            <span className="seller-profile__rating-value">
                                {profile.averageRating ? profile.averageRating.toFixed(1) : 'Нет оценок'}
                            </span>
                            <span className="seller-profile__rating-count">
                                ({profile.reviewsCount} {reviewWord(profile.reviewsCount)})
                            </span>
                        </div>
                        {formatRegisteredAt(profile.registeredAt) && (
                            <p className="seller-profile__registered">На Dealzy с {formatRegisteredAt(profile.registeredAt)}</p>
                        )}
                        {profile.telegramNick && (
                            <p className="seller-profile__telegram">Telegram: {profile.telegramNick}</p>
                        )}
                    </div>

                    {!isOwnProfile && (
                        <div className="seller-profile__contact-btns">
                            <button className="seller-profile__btn-phone" onClick={handleShowPhone}>
                                {phoneRevealed && profile.phoneNumber ? profile.phoneNumber : 'Показать телефон'}
                            </button>
                            <button className="seller-profile__btn-chat" onClick={handleStartChat} disabled={chatBusy}>
                                Написать
                            </button>
                        </div>
                    )}
                </div>

                <div className="seller-profile__tabs">
                    <button
                        className={`seller-profile__tab ${activeTab === 'reviews' ? 'seller-profile__tab--active' : ''}`}
                        onClick={() => setActiveTab('reviews')}
                    >
                        Отзывы {profile.reviewsCount ? `(${profile.reviewsCount})` : ''}
                    </button>
                    <button
                        className={`seller-profile__tab ${activeTab === 'ads' ? 'seller-profile__tab--active' : ''}`}
                        onClick={() => setActiveTab('ads')}
                    >
                        Объявления {ads.length ? `(${ads.length})` : ''}
                    </button>
                </div>

                {activeTab === 'reviews' && (
                    <div className="seller-profile__section">
                        {!isOwnProfile && (
                            <div className="seller-profile__review-form">
                                <StarInput value={formRating} onChange={setFormRating} />
                                <textarea
                                    className="seller-profile__review-textarea"
                                    placeholder="Поделитесь впечатлением от сделки с этим пользователем"
                                    value={formContent}
                                    onChange={e => setFormContent(e.target.value)}
                                    rows={3}
                                />
                                <div className="seller-profile__review-form-actions">
                                    <button
                                        className="seller-profile__review-submit"
                                        onClick={submitReview}
                                        disabled={formBusy || formRating < 1}
                                    >
                                        {formBusy ? 'Отправка...' : (profile.myRating ? 'Обновить отзыв' : 'Оставить отзыв')}
                                    </button>
                                    {profile.myRating && (
                                        <button
                                            className="seller-profile__review-delete"
                                            onClick={handleDeleteReview}
                                            disabled={deleteBusy}
                                        >
                                            {deleteBusy ? 'Удаление...' : 'Удалить отзыв'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="seller-profile__reviews-list">
                            {reviews.map(r => (
                                <div className="seller-profile__review" key={r.id}>
                                    <div className="seller-profile__review-avatar">
                                        {r.authorAvatar
                                            ? <img src={r.authorAvatar} alt={r.authorName} />
                                            : (r.authorName?.charAt(0).toUpperCase() || '?')
                                        }
                                    </div>
                                    <div className="seller-profile__review-body">
                                        <div className="seller-profile__review-header">
                                            <span className="seller-profile__review-author">{r.authorName}</span>
                                            <StarRating value={r.rating} size={14} />
                                        </div>
                                        {r.content && <p className="seller-profile__review-content">{r.content}</p>}
                                        <span className="seller-profile__review-date">
                                            {new Date(r.createdAt).toLocaleDateString('ru-RU')}
                                            {r.isMine ? ' · ваш отзыв' : ''}
                                        </span>
                                        {r.isMine && (
                                            <button
                                                className="seller-profile__review-delete-inline"
                                                onClick={handleDeleteReview}
                                                disabled={deleteBusy}
                                            >
                                                удалить
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {!reviewsLoading && reviews.length === 0 && (
                                <p className="seller-profile__empty">Пока нет отзывов</p>
                            )}
                        </div>

                        {reviewsHasMore && (
                            <button className="seller-profile__show-more" onClick={handleShowMoreReviews} disabled={reviewsLoading}>
                                Показать ещё
                            </button>
                        )}
                    </div>
                )}

                {activeTab === 'ads' && (
                    <div className="seller-profile__section">
                        {adsLoading ? (
                            <p className="seller-profile__empty">Загрузка...</p>
                        ) : ads.length === 0 ? (
                            <p className="seller-profile__empty">Нет активных объявлений</p>
                        ) : (
                            <div className="seller-profile__listings-grid">
                                {ads.map(ad => <AdCard key={ad.id} ad={ad} view="grid" />)}
                            </div>
                        )}
                    </div>
                )}

            </div>
            <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
        </div>
    );
};

export default SellerProfilePage;
